import React, { useState, useEffect } from 'react';
import { BarChart3, Eye, Smartphone, Monitor, Tablet, Calendar, Clock, MapPin, Filter, Download, RefreshCw } from 'lucide-react';

const MenuAccessLogsUI = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('');
  const [restaurantFilter, setRestaurantFilter] = useState('');

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Load logs from API
  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/MenuAccessLogs`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      } else {
        setError('Erişim logları yüklenirken hata oluştu');
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="h-4 w-4" />;
      case 'tablet':
        return <Tablet className="h-4 w-4" />;
      case 'desktop':
        return <Monitor className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  const getDeviceColor = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return 'bg-green-100 text-green-800';
      case 'tablet':
        return 'bg-blue-100 text-blue-800';
      case 'desktop':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}s ${minutes}d ${remainingSeconds}sn`;
    } else if (minutes > 0) {
      return `${minutes}d ${remainingSeconds}sn`;
    } else {
      return `${remainingSeconds}sn`;
    }
  };

  const getLocationInfo = (ipAddress) => {
    // This is a simplified example. In real app, you would use IP geolocation service
    if (ipAddress?.startsWith('192.168') || ipAddress?.startsWith('10.') || ipAddress?.startsWith('172.')) {
      return 'Yerel Ağ';
    }
    return 'Dış Ağ';
  };

  // Filtered logs
  const filteredLogs = logs.filter(log => {
    const matchesDate = !dateFilter || log.accessDate?.startsWith(dateFilter);
    const matchesDevice = !deviceFilter || log.deviceType?.toLowerCase() === deviceFilter.toLowerCase();
    const matchesRestaurant = !restaurantFilter || log.restaurantId === restaurantFilter;
    
    return matchesDate && matchesDevice && matchesRestaurant;
  });

  // Analytics calculations
  const analytics = {
    totalAccess: filteredLogs.length,
    uniqueIPs: new Set(filteredLogs.map(log => log.ipAddress)).size,
    avgSessionDuration: filteredLogs.reduce((sum, log) => sum + (log.sessionDuration || 0), 0) / filteredLogs.length || 0,
    deviceBreakdown: {
      mobile: filteredLogs.filter(log => log.deviceType?.toLowerCase() === 'mobile').length,
      tablet: filteredLogs.filter(log => log.deviceType?.toLowerCase() === 'tablet').length,
      desktop: filteredLogs.filter(log => log.deviceType?.toLowerCase() === 'desktop').length
    },
    hourlyDistribution: {}
  };

  // Calculate hourly distribution
  filteredLogs.forEach(log => {
    if (log.accessDate) {
      const hour = new Date(log.accessDate).getHours();
      analytics.hourlyDistribution[hour] = (analytics.hourlyDistribution[hour] || 0) + 1;
    }
  });

  const uniqueDeviceTypes = [...new Set(logs.map(log => log.deviceType).filter(Boolean))];
  const uniqueRestaurants = [...new Set(logs.map(log => log.restaurantId).filter(Boolean))];

  const exportToCSV = () => {
    const headers = ['Tarih', 'Restoran ID', 'QR Code ID', 'IP Adresi', 'Cihaz Tipi', 'Oturum Süresi', 'User Agent'];
    const csvContent = [
      headers.join(','),
      ...filteredLogs.map(log => [
        log.accessDate || '',
        log.restaurantId || '',
        log.qrCodeId || '',
        log.ipAddress || '',
        log.deviceType || '',
        log.sessionDuration || '',
        `"${log.userAgent || ''}"` // Quotes for user agent strings
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `menu-access-logs-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Menü Erişim Logları</h1>
                <p className="text-gray-600">QR kod taramalarını ve menü erişimlerini analiz edin</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={fetchLogs}
                disabled={loading}
                className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:bg-gray-400"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Yenile</span>
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>CSV İndir</span>
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Toplam Erişim</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.totalAccess}</p>
              </div>
              <Eye className="h-8 w-8 text-indigo-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Benzersiz IP</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.uniqueIPs}</p>
              </div>
              <MapPin className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ort. Oturum</p>
                <p className="text-2xl font-bold text-gray-900">{formatDuration(Math.round(analytics.avgSessionDuration))}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Mobil Oran</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.totalAccess > 0 ? Math.round((analytics.deviceBreakdown.mobile / analytics.totalAccess) * 100) : 0}%
                </p>
              </div>
              <Smartphone className="h-8 w-8 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Device Breakdown Chart */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Cihaz Dağılımı</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Smartphone className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.deviceBreakdown.mobile}</p>
              <p className="text-sm text-gray-600">Mobil</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Tablet className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.deviceBreakdown.tablet}</p>
              <p className="text-sm text-gray-600">Tablet</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Monitor className="h-6 w-6 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.deviceBreakdown.desktop}</p>
              <p className="text-sm text-gray-600">Masaüstü</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Filtreler</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tarih
              </label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cihaz Tipi
              </label>
              <select
                value={deviceFilter}
                onChange={(e) => setDeviceFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Tüm Cihazlar</option>
                {uniqueDeviceTypes.map(deviceType => (
                  <option key={deviceType} value={deviceType}>
                    {deviceType}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Restoran
              </label>
              <select
                value={restaurantFilter}
                onChange={(e) => setRestaurantFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Tüm Restoranlar</option>
                {uniqueRestaurants.map(restaurantId => (
                  <option key={restaurantId} value={restaurantId}>
                    {restaurantId}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Erişim Logları ({filteredLogs.length} kayıt)
            </h3>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Yükleniyor...</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {dateFilter || deviceFilter || restaurantFilter ? 'Filtre kriterlerine uygun log bulunamadı' : 'Henüz erişim logu bulunmuyor'}
                </h3>
                <p className="text-gray-600">
                  {dateFilter || deviceFilter || restaurantFilter ? 'Farklı filtreler deneyebilirsiniz' : 'QR kod taramaları burada görünecek'}
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tarih & Saat
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Restoran
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cihaz
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP Adresi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Oturum Süresi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User Agent
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLogs.map((log) => (
                    <tr key={log.logId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">
                            {formatDate(log.accessDate)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {log.restaurantId}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDeviceColor(log.deviceType)}`}>
                            {getDeviceIcon(log.deviceType)}
                            <span className="ml-1">{log.deviceType || 'Bilinmiyor'}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{log.ipAddress}</div>
                        <div className="text-xs text-gray-500">{getLocationInfo(log.ipAddress)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDuration(log.sessionDuration)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={log.userAgent}>
                          {log.userAgent || '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuAccessLogsUI;